import {
  DeleteOutlined,
  HistoryOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { Button, DatePicker, Form, Input } from 'antd';
import dayjs from 'dayjs';
import { cn } from '@/libs/utils';
import { DATE_FORMATS } from '@/utils/formUtils';
import {
  formatSpan,
  spanBetween,
  workExperienceTotal,
} from '../../workExperience';
import { useEmployeeGeneralDataForm } from '../EmployeeGeneralDataFormContext';

const API = 'additionalPersonalInfo';
const fmt = (d) =>
  d && dayjs(d).isValid() ? dayjs(d).format(DATE_FORMATS.DISPLAY) : '';

function Title({ children, action }) {
  return (
    <div className="mb-2 flex items-center justify-between gap-2">
      <h3 className="text-[11px] font-extrabold uppercase tracking-[0.08em] text-zinc-400">
        {children}
      </h3>
      {action}
    </div>
  );
}

/**
 * The "Work experience" tile of the employee profile: the total as of today
 * on top (what was entered plus what the app has counted since), the time in
 * this company, then the entered experience and the previous employments.
 * Previous experience is optional: without it (or without the date to count
 * on from) the app counts from the employment start. Same fields, rules and
 * save as the work-experience part of EmployeeAdditionalPersonalInfoForm,
 * which passes its list handlers in and steps aside on the profile.
 */
export default function EmployeeExperienceProfilePanel({
  previousEmployments,
  onAddEmployment,
  onRemoveEmployment,
  onFieldChange,
  onExperienceChange,
}) {
  const { endDate, form, readOnly, startDate: employmentStart, t } =
    useEmployeeGeneralDataForm();
  const f = (key) => t(`formValidation.${key}`, { ns: 'forms' });
  const tp = (key, opts) =>
    t(`employeeProfile.experienceUi.${key}`, { ns: 'translations', ...opts });

  const workExperience = Form.useWatch([API, 'workExperience'], {
    form,
    preserve: true,
  });
  const countingDate = Form.useWatch([API, 'workExperienceStartDate'], {
    form,
    preserve: true,
  });
  const employments =
    Form.useWatch([API, 'previousEmployments'], { form, preserve: true }) || [];
  const exp = workExperienceTotal(
    workExperience,
    countingDate,
    endDate,
    employmentStart
  );
  const hasTotal = !!exp;
  const hasEntered = !!exp?.hasEntered;
  const startsLater = !!exp?.from && !exp.since;

  const totalBreakdown = () => {
    if (!exp) return '';
    if (!exp.from) return tp('noCountingDate');
    if (startsLater) return tp('startsOn', { date: fmt(exp.from) });
    const dates = { from: fmt(exp.from), until: fmt(exp.until) };
    if (!hasEntered) {
      return tp(exp.isCounting ? 'allInFirmToday' : 'allInFirmEnded', dates);
    }
    return tp(exp.isCounting ? 'breakdownToday' : 'breakdownEnded', {
      ...dates,
      entered: formatSpan(exp.entered, t),
      since: formatSpan(exp.since, t),
    });
  };

  const experienceInput = (key, max) => (
    <Form.Item name={[API, 'workExperience', key]} className="!mb-0">
      <Input
        type="number"
        min={0}
        max={max}
        placeholder={f(key)}
        suffix={f(key)}
        onChange={() => {
          onExperienceChange();
          onFieldChange();
        }}
      />
    </Form.Item>
  );

  return (
    <div className="grid max-w-[620px] gap-4">
      <section className="flex items-center gap-3 rounded-xl border border-[rgba(var(--accentColor),0.35)] bg-[rgba(var(--accentColor),0.08)] p-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-[rgba(var(--accentColor),0.14)] text-[18px] text-accent">
          <HistoryOutlined />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[11.5px] font-semibold text-zinc-600">
            {tp('totalTitle')}
          </div>
          <div
            className={cn(
              'text-[22px] font-extrabold leading-tight',
              hasTotal ? 'text-accent' : 'text-zinc-400'
            )}
          >
            {hasTotal ? formatSpan(exp.total, t) : tp('notEntered')}
          </div>
          {hasTotal && (
            <div className="mt-0.5 text-[12px] text-zinc-600">
              {totalBreakdown()}
            </div>
          )}
        </div>
      </section>

      {exp?.employmentStart && (
        <section className="flex items-baseline justify-between gap-3 rounded-xl border border-zinc-200 bg-white px-3 py-2.5">
          <span className="text-[12.5px] font-semibold text-zinc-600">
            {tp('inFirmTitle')}
          </span>
          <span className="min-w-0 text-right leading-tight">
            <span className="block text-[15px] font-bold text-zinc-900">
              {exp.inFirm ? formatSpan(exp.inFirm, t) : '—'}
            </span>
            <span className="block text-[11.5px] text-zinc-500">
              {exp.inFirm
                ? tp(exp.isCounting ? 'inFirmToday' : 'inFirmEnded', {
                    from: fmt(exp.employmentStart),
                    until: fmt(exp.until),
                  })
                : tp('startsOn', { date: fmt(exp.employmentStart) })}
            </span>
          </span>
        </section>
      )}

      <section>
        <Title>{tp('groupBefore')}</Title>
        <div className="rounded-xl border border-zinc-200 bg-white p-3">
          {readOnly && (
            <div className="flex items-baseline justify-between gap-3">
              <span
                className={cn(
                  'text-[15px] font-bold',
                  hasEntered ? 'text-zinc-900' : 'text-zinc-400'
                )}
              >
                {hasEntered ? formatSpan(exp.entered, t) : tp('notEntered')}
              </span>
              {hasEntered && exp.from && (
                <span className="text-[12px] text-zinc-500">
                  {tp(exp.fromDefault ? 'countingFromStart' : 'countingFrom', {
                    date: fmt(exp.from),
                  })}
                </span>
              )}
            </div>
          )}
          {/* Kept mounted while viewing (only hidden), so the form never
              sees the values as missing. */}
          <div className={cn('grid gap-3', readOnly && 'hidden')}>
            <p className="text-[12px] text-zinc-500">{tp('beforeHelp')}</p>
            <div className="grid grid-cols-3 gap-2">
              {experienceInput('years')}
              {experienceInput('months', 11)}
              {experienceInput('days', 30)}
            </div>
            {/* Optional: left empty, counting starts with the employment
                start, so the time in the company always counts. */}
            {hasEntered && (
              <Form.Item
                name={[API, 'workExperienceStartDate']}
                label={f('workExperienceStartDate')}
                className="!mb-0"
                extra={
                  exp?.employmentStart
                    ? tp('countingDefault', {
                        date: fmt(exp.employmentStart),
                      })
                    : undefined
                }
              >
                <DatePicker
                  className="w-full"
                  format={DATE_FORMATS.DISPLAY}
                  placeholder={f('workExperienceStartDatePlaceholder')}
                  onChange={onFieldChange}
                />
              </Form.Item>
            )}
          </div>
        </div>
      </section>

      <section>
        <Title
          action={
            !readOnly && (
              <Button
                type="dashed"
                size="small"
                icon={<PlusOutlined />}
                onClick={onAddEmployment}
              >
                {f('add')}
              </Button>
            )
          }
        >
          {f('previousEmployments')}
        </Title>
        <div className="rounded-xl border border-zinc-200 bg-white">
          {!previousEmployments?.length && (
            <div className="px-3 py-2.5 text-[12.5px] italic text-zinc-400">
              {tp('noPrevious')}
            </div>
          )}
          {previousEmployments?.map((_, index) => {
            const row = employments[index] || {};
            const span = spanBetween(row.startDate, row.endDate, {
              inclusive: true,
            });
            const name = (key) => [API, 'previousEmployments', index, key];
            return (
              <div
                key={index}
                className="border-t border-zinc-100 px-3 py-2 first:border-t-0"
              >
                {readOnly && (
                  <div className="flex items-center gap-3">
                    <span className="min-w-0 flex-1 leading-tight">
                      <span className="block truncate text-[12.5px] font-semibold text-zinc-800">
                        {row.company || '—'}
                      </span>
                      <span className="block text-[11.5px] text-zinc-500">
                        {fmt(row.startDate) || '?'} – {fmt(row.endDate) || '?'}
                      </span>
                    </span>
                    <span className="shrink-0 text-[13px] font-bold text-zinc-900">
                      {span ? formatSpan(span, t) : '—'}
                    </span>
                  </div>
                )}
                <div className={cn('grid gap-2', readOnly && 'hidden')}>
                  <Form.Item
                    name={name('company')}
                    label={tp('employer')}
                    className="!mb-0"
                  >
                    <Input
                      placeholder={tp('employer')}
                      onChange={onFieldChange}
                    />
                  </Form.Item>
                  <div className="grid grid-cols-[1fr_1fr_auto] items-end gap-2">
                    <Form.Item
                      name={name('startDate')}
                      label={t('startDate', { ns: 'forms' })}
                      className="!mb-0"
                    >
                      <DatePicker
                        className="w-full"
                        format={DATE_FORMATS.DISPLAY}
                        placeholder={t('startDate', { ns: 'forms' })}
                        onChange={onFieldChange}
                      />
                    </Form.Item>
                    <Form.Item
                      name={name('endDate')}
                      label={t('endDate', { ns: 'forms' })}
                      className="!mb-0"
                    >
                      <DatePicker
                        className="w-full"
                        format={DATE_FORMATS.DISPLAY}
                        placeholder={t('endDate', { ns: 'forms' })}
                        onChange={onFieldChange}
                      />
                    </Form.Item>
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      aria-label={t('delete', { ns: 'forms' })}
                      onClick={() => onRemoveEmployment(index)}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
